import {
  Badge,
  Chip,
  Divider,
  Grid,
  IconButton,
  Paper,
  Tooltip,
  Typography
} from '@material-ui/core';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import {
  AddCommentOutlined,
  ChatBubbleRounded,
  CloudUploadOutlined,
  GetAppOutlined,
  GetAppRounded,
  LockRounded,
  VisibilityOutlined
} from '@material-ui/icons';
import clsx from 'clsx';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { Link as RRLink } from 'react-router-dom';
import { RootState } from '../../app/store';
import { ContentTypeLabel } from '../../common/enum/ContentTypeLabel';
import DocRevisionInfoCard from '../../components/DocRevisionInfoCard';
import ProjectInfoCard from '../../components/ProjectInfoCard';
import { ENABLE_ANNOTATION, SHOW_ANNOTATION_BETA } from '../../constants';
import { useAppDispatch } from '../../hooks/useAppRudux';
import Breadcrumb from '../Breadcrumb';
import loadingSlice from '../Loading/loadingSlice';
import {
  ActionNameLabel,
  getProjectDocumentVersionFileAnnotationAsync,
  getProjectDocumentVersionFileOnlyAsync
} from '../ProjectDocumentVersionFile/projectDocumentVersionFileSlice';
import { getProjectDocumentRevisionFileAsync } from './projectDocumentRevisionFileSlice';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      margin: theme.spacing(1.5, 0, 12, 0)
    },
    title: {
      margin: theme.spacing(1.5, 0, 2, 0),
      [theme.breakpoints.down('xs')]: {
        textAlign: 'center',
        margin: theme.spacing(3.5, 0, 4, 0)
      }
    },
    card: {
      padding: theme.spacing(2),
      marginBottom: theme.spacing(2),
      borderRadius: theme.spacing(2)
    },
    paper: {
      padding: theme.spacing(1, 1.5),
      marginBottom: theme.spacing(1),
      borderRadius: theme.spacing(1),
      '&:last-child': {
        marginBottom: 0
      }
    },
    divider: {
      margin: theme.spacing(0.5, 0, 2, 0),
      width: theme.spacing(4)
    },
    dividerVertical: {
      margin: theme.spacing(1.5, 0.5)
    },
    showInformation: {
      [theme.breakpoints.down('sm')]: {
        display: 'none'
      }
    },
    flexCenter: {
      display: 'flex',
      alignItems: 'center'
    },
    flexGrow: {
      flexGrow: 1
    },
    iconFile: {
      color: theme.palette.text.hint,
      fontSize: 14,
      margin: theme.spacing(0, 1)
    },
    badgeContentIcon: {
      backgroundColor: theme.palette.background.paper,
      borderRadius: theme.spacing(0.25),
      padding: theme.spacing(0.125),
      width: 16,
      height: 16
    },
    filenameBox: {
      marginLeft: theme.spacing(1.5)
    },
    fileLog: {
      margin: theme.spacing(0, 0, 1, 2)
    },
    chip: {
      margin: theme.spacing(0, 1, 0, -0.5),
      padding: theme.spacing(0, 0.5, 0, 0.75)
    },
    addCommentIcon: {
      transform: 'scaleX(-1)',
      padding: theme.spacing(0.25),
      marginRight: theme.spacing(-0.75)
    }
  })
);

export default function DocumentFileView() {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const { selectedProjectDocumentRevisionFile } = useSelector(
    (state: RootState) => state.projectDocumentRevisionFiles
  );
  const { selectedProjectDocumentRevision } = useSelector(
    (state: RootState) => state.projectDocumentRevisions
  );
  const { selectedProjectDocument } = useSelector(
    (state: RootState) => state.projectDocuments
  );
  const { selectedProject } = useSelector((state: RootState) => state.projects);

  const params = useParams<{ fileId: string }>();
  const projectDocumentRevisionFileId = Number.parseInt(params.fileId, 10);

  const isDownloadWithWatermark = Boolean(
    selectedProjectDocumentRevisionFile?.s3ContentType ===
      ContentTypeLabel.pdf ||
      selectedProjectDocumentRevisionFile?.s3ContentType ===
        ContentTypeLabel.word ||
      selectedProjectDocumentRevisionFile?.s3ContentType ===
        ContentTypeLabel.excel
  );

  const enableAnnotation = false;
  // ENABLE_ANNOTATION &&
  // !!selectedProject?.projectNo &&
  // (
  //   process.env.REACT_APP_PILOT_ANNOTATION_PROJECT_NO?.split(',').filter(
  //     Boolean
  //   ) ?? []
  // ).includes(selectedProject.projectNo);

  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    dispatch(
      getProjectDocumentRevisionFileAsync(projectDocumentRevisionFileId)
    );
  }, []);

  function getChipIcon(actionName: ActionNameLabel) {
    let iconChip;
    switch (actionName) {
      case ActionNameLabel.upload:
        iconChip = <CloudUploadOutlined />;
        break;
      case ActionNameLabel.download:
        iconChip = <GetAppOutlined />;
        break;
      case ActionNameLabel.preview:
        iconChip = <VisibilityOutlined />;
        break;
      case ActionNameLabel.onlineReview:
        iconChip = <AddCommentOutlined className={classes.addCommentIcon} />;
    }
    return iconChip;
  }

  async function onDownload({
    projectDocumentVersionFileId,
    contentType,
    filename
  }: {
    projectDocumentVersionFileId: number;
    contentType: string;
    filename: string;
  }) {
    try {
      setLoading(true);
      await dispatch(
        getProjectDocumentVersionFileOnlyAsync({
          projectDocumentVersionFileId,
          contentType,
          filename,
          positions: ['Center', 'Center'],
          rotations: [45, 45],
          colors: ['#EB4034', '#EB4034'],
          actionDate: moment().toDate()
        })
      ).unwrap();
      dispatch(
        getProjectDocumentRevisionFileAsync(projectDocumentRevisionFileId)
      );
      dispatch(
        loadingSlice.actions.success({
          message: t('projectDocumentRevisionFileView.messages.fileDownloadedSuccessfully') as string
        })
      );
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    } catch {
      setLoading(false);
    }
  }

  async function onDownloadAnnotation({
    projectDocumentVersionFileAnnotationId,
    contentType,
    filename,
    isDownload = true
  }: {
    projectDocumentVersionFileAnnotationId: number;
    contentType: string;
    filename: string;
    isDownload: boolean;
  }) {
    try {
      setLoading(true);
      // TODO: Revise annotation link here
      // await dispatch(
      //   getProjectDocumentVersionFileAnnotationAsync({
      //     projectDocumentVersionFileAnnotationId,
      //     contentType,
      //     filename,
      //     isDownload,
      //     isPreview: !Boolean(isDownload)
      //   })
      // ).unwrap();
      dispatch(
        loadingSlice.actions.success({
          message: t('projectDocumentRevisionFileView.messages.fileDownloadedSuccessfully') as string
        })
      );
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    } catch {
      setLoading(false);
    }
  }

  return (
    <div className={classes.root}>
      <Breadcrumb
        name="documentFileName"
        data={{
          projectName: selectedProject?.name,
          projectId: selectedProject?.id,
          documentName: selectedProjectDocument?.name,
          documentId: selectedProjectDocument?.id,
          revisionName: `Purpose: ${selectedProjectDocumentRevision?.purpose.abbreviation}, Revision: ${selectedProjectDocumentRevision?.revision.name}`,
          revisionId: selectedProjectDocumentRevision?.id,
          documentFileName: selectedProjectDocumentRevisionFile?.name,
          documentFileId: selectedProjectDocumentRevisionFile?.id
        }}
      />
      <Typography className={classes.title} component="p" variant="h4">
        {t('projectDocumentRevisionFileView.title')}
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={3} className={classes.showInformation}>
          {selectedProject && <ProjectInfoCard project={selectedProject} />}
          {selectedProjectDocument && selectedProjectDocumentRevision && (
            <DocRevisionInfoCard
              document={selectedProjectDocument}
              docRevision={selectedProjectDocumentRevision}
            />
          )}
        </Grid>
        <Grid item xs={12} md={9}>
          <Paper className={classes.card}>
            <Typography variant="subtitle1" gutterBottom>
              {t('projectDocumentRevisionFileView.sections.history')}
            </Typography>
            <Divider className={classes.divider} />
            {selectedProjectDocumentRevisionFile?.projectDocumentVersionFiles.map(
              (projectDocumentVersionFile) => {
                return (
                  <Paper
                    className={classes.paper}
                    variant="outlined"
                    key={projectDocumentVersionFile.id}
                  >
                    <div className={classes.flexCenter}>
                      <span>
                        {isDownloadWithWatermark ? (
                          <Tooltip title={t('projectDocumentRevisionFileView.tooltips.download') as string} arrow>
                            <IconButton
                              size="small"
                              component={RRLink}
                              to={`/document-versions/${projectDocumentVersionFile.id}`}
                            >
                              <GetAppRounded />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title={t('projectDocumentRevisionFileView.tooltips.download') as string} arrow>
                            <span>
                              <IconButton
                                onClick={() =>
                                  onDownload({
                                    projectDocumentVersionFileId:
                                      projectDocumentVersionFile.id,
                                    contentType:
                                      selectedProjectDocumentRevisionFile.s3ContentType,
                                    filename:
                                      selectedProjectDocumentRevisionFile.name
                                  })
                                }
                                size="small"
                                disabled={loading}
                              >
                                <GetAppRounded />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </span>
                      {selectedProjectDocumentRevisionFile.s3ContentType ===
                        ContentTypeLabel.pdf &&
                        enableAnnotation && (
                          <>
                            <Divider
                              className={classes.dividerVertical}
                              orientation="vertical"
                              flexItem
                            />
                            <Tooltip
                              title={`${t('projectDocumentRevisionFileView.tooltips.downloadWithComments')}${
                                SHOW_ANNOTATION_BETA ? ` (${t('projectDocumentRevisionFileView.tooltips.beta')})` : ''
                              }`}
                              arrow
                            >
                              <span>
                                <IconButton
                                  onClick={() => {
                                    if (projectDocumentVersionFile.annotation)
                                      onDownloadAnnotation({
                                        projectDocumentVersionFileAnnotationId:
                                          projectDocumentVersionFile.annotation
                                            .annotationFileId,
                                        contentType:
                                          selectedProjectDocumentRevisionFile.s3ContentType,
                                        filename:
                                          selectedProjectDocumentRevisionFile.name,
                                        isDownload: true
                                      });
                                  }}
                                  size="small"
                                  disabled={
                                    loading ||
                                    !Boolean(
                                      projectDocumentVersionFile.annotation
                                    )
                                  }
                                >
                                  <Badge
                                    overlap="circle"
                                    anchorOrigin={{
                                      vertical: 'bottom',
                                      horizontal: 'right'
                                    }}
                                    badgeContent={
                                      <ChatBubbleRounded
                                        className={classes.badgeContentIcon}
                                      />
                                    }
                                  >
                                    <GetAppRounded />
                                  </Badge>
                                </IconButton>
                              </span>
                            </Tooltip>
                          </>
                        )}
                      <div
                        className={clsx(classes.flexGrow, classes.filenameBox)}
                      >
                        <Typography variant="body1">
                          {selectedProjectDocumentRevisionFile.name}
                        </Typography>
                        <div className={classes.flexCenter}>
                          <Typography variant="caption" color="textSecondary">
                            {t('projectDocumentRevisionFileView.labels.uploadedAt')}{' '}
                            {moment(
                              projectDocumentVersionFile.createdAt
                            ).format('lll')}{' '}
                            • {t('projectDocumentRevisionFileView.labels.version')} {projectDocumentVersionFile.versionNo}
                          </Typography>
                          {!selectedProjectDocumentRevisionFile.isPublic && (
                            <Tooltip title={t('projectDocumentRevisionFileView.tooltips.encOnly') as string} arrow>
                              <LockRounded className={classes.iconFile} />
                            </Tooltip>
                          )}
                        </div>
                      </div>
                    </div>
                    <Divider className={classes.divider} />
                    {projectDocumentVersionFile.projectDocumentVersionFileLogs.map(
                      (projectDocumentVersionFileLog) => {
                        const name = `${projectDocumentVersionFileLog.firstName} ${projectDocumentVersionFileLog.lastName}`;
                        return (
                          <div
                            className={classes.fileLog}
                            key={projectDocumentVersionFileLog.id}
                          >
                            <Typography>{name}</Typography>
                            <Chip
                              className={classes.chip}
                              label={projectDocumentVersionFileLog.actionName}
                              variant="outlined"
                              size="small"
                              icon={getChipIcon(
                                projectDocumentVersionFileLog.actionName
                              )}
                            />
                            <Tooltip
                              title={moment(
                                projectDocumentVersionFileLog.createdAt
                              ).format('lll')}
                              arrow
                            >
                              <Typography
                                variant="caption"
                                color="textSecondary"
                              >
                                {moment(
                                  projectDocumentVersionFileLog.createdAt
                                ).fromNow()}
                              </Typography>
                            </Tooltip>
                          </div>
                        );
                      }
                    )}
                  </Paper>
                );
              }
            )}
          </Paper>
        </Grid>
      </Grid>
    </div>
  );
}
